import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';

interface EvidenceSource {
  type: string;
  count: number;
}

interface Strength {
  id: string;
  name: string;
  confidence: string;
  confirmedBy: string;
  confirmedDate: string;
  evidenceSummary: string;
  evidenceCount: number;
  sources: EvidenceSource[];
}

interface StrengthsSectionProps {
  strengths: Strength[];
  userRole: 'employee' | 'manager';
  onStrengthClick: (strength: Strength) => void;
}

const StrengthsSection: React.FC<StrengthsSectionProps> = ({ strengths, userRole, onStrengthClick }) => {
  const { t } = useTranslation();

  const getConfidenceColor = (confidence: string) => {
    if (confidence?.includes('Consistently')) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
    }
    if (confidence?.includes('Frequently')) {
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
    }
    return 'bg-muted text-muted-foreground border-border';
  };

  const getSourceIcon = (type: string) => {
    const icons: Record<string, string> = {
      recognition: 'Award',
      review: 'FileText',
      coaching: 'MessageCircle'
    };
    return icons?.[type] || 'Link';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('Strengths')}</h3>
          <p className="text-xs text-muted-foreground mt-1">{t('Recurring patterns that indicate natural talent or aptitude')}</p>
        </div>
        <p className="text-sm text-muted-foreground">{strengths?.length} {strengths?.length !== 1 ? t('strengths') : t('strength')}</p>
      </div>
      <div className="space-y-4">
        {strengths?.map((strength) => (
          <div
            key={strength?.id}
            className="border border-border rounded-lg p-4 hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer bg-gradient-to-r from-card to-blue-50/30 dark:to-blue-950/20"
            onClick={() => onStrengthClick(strength)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="Sparkles" size={20} className="text-primary" />
                  <h4 className="text-base font-semibold text-foreground">{strength?.name}</h4>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center">
                    <Icon name="CheckCircle" size={12} className="me-1" />
                    {t('Confirmed by')} {strength?.confirmedBy}
                  </span>
                  <span className="flex items-center">
                    <Icon name="Calendar" size={12} className="me-1" />
                    {formatDate(strength?.confirmedDate)}
                  </span>
                </div>
              </div>

              {/* Confidence Indicator */}
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getConfidenceColor(strength?.confidence)}`}>
                {strength?.confidence}
              </span>
            </div>

            {/* Evidence Summary */}
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-sm text-foreground leading-relaxed mb-3">{strength?.evidenceSummary}</p>

              {/* Evidence Sources */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {strength?.sources?.map((source, index) => (
                    <div key={index} className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Icon name={getSourceIcon(source?.type) as any} size={14} className="text-muted-foreground" />
                      <span className="capitalize">{source?.type}:</span>
                      <span className="font-medium">{source?.count}</span>
                    </div>
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{strength?.evidenceCount} {t('total evidence items')}</span>
              </div>
            </div>

            {/* Read-only indicator for employees */}
            {userRole === 'employee' && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Lock" size={12} />
                  <span>{t('Strengths are confirmed by managers based on observed patterns')}</span>
                </div>
              </div>
            )}
          </div>
        ))}

        {strengths?.length === 0 && (
          <div className="text-center py-12 bg-muted rounded-lg border border-border">
            <Icon name="Sparkles" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-foreground font-medium mb-1">{t('No strengths identified yet')}</p>
            <p className="text-sm text-muted-foreground">
              {t('Strengths emerge from consistent demonstration of capability')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrengthsSection;
